// ============================================================================
// PRICING CATALOG — Lovable-managed payments
// ----------------------------------------------------------------------------
// priceId values are human-readable price IDs (e.g. "starter_monthly").
// The checkout resolves them to real payment price IDs via get-paddle-price.
// Test and live environments are selected automatically from the client
// token in .env.development / .env.production (see src/lib/paddle.ts).
// ============================================================================

export type PlanId = "starter" | "growth" | "pro";

export interface Plan {
  id: PlanId;
  name: string;
  description: string;
  price: number; // USD per month
  priceId: string; // Paddle price ID (pri_...)
  features: { text: string; included: boolean }[];
  popular?: boolean;
}

export const PLANS: Record<PlanId, Plan> = {
  starter: {
    id: "starter",
    name: "Starter",
    description: "For solo operators getting started",
    price: 29,
    priceId: "starter_monthly",
    features: [
      { text: "Smart Data Entry (all 4 input modes)", included: true },
      { text: "Up to 5,000 records/month", included: true },
      { text: "AI data cleaning & insights", included: true },
      { text: "Email support", included: true },
      { text: "Predictive models", included: false },
    ],
  },
  growth: {
    id: "growth",
    name: "Growth",
    description: "For growing teams that need automation",
    price: 79,
    priceId: "growth_monthly",
    features: [
      { text: "Everything in Starter", included: true },
      { text: "Up to 25,000 records/month", included: true },
      { text: "Predictive models & forecasting", included: true },
      { text: "CRM integrations (HubSpot, Zoho)", included: true },
      { text: "Priority support", included: true },
    ],
    popular: true,
  },
  pro: {
    id: "pro",
    name: "Pro",
    description: "For data-heavy organisations",
    price: 199,
    priceId: "pro_monthly",
    features: [
      { text: "Everything in Growth", included: true },
      { text: "Unlimited records", included: true },
      { text: "Workflow automation & scheduled reports", included: true },
      { text: "All integrations + custom sources", included: true },
      { text: "24/7 dedicated support", included: true },
    ],
  },
};

export const formatUsd = (n: number) => `$${n.toLocaleString("en-US")}`;

export const isPaddleConfigured = () =>
  !!import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN;

export const planByPriceId = (priceId?: string | null): Plan | undefined =>
  Object.values(PLANS).find((p) => p.priceId === priceId);
